import {
	Area,
	Bar,
	CartesianGrid,
	ComposedChart,
	LabelList,
	Legend,
	Line,
	ReferenceLine,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	type ChartData,
	type ChartRow,
	type ChartSeries,
	cartesianAxes,
	dashFor,
	isRtl,
	REDUCED_MOTION_QUERY,
	seriesColor,
	useMediaQuery,
} from "./data";

/**
 * The axis charts (EP13-S04): a trend over days, and bars. Recharts draws them
 * in SVG, so every stroke and fill below is a `--viz-*` variable and follows
 * the theme switch with no re-render.
 *
 * In Arabic time runs right to left: the category axis is reversed and the
 * value axis stands on the right (`cartesianAxes`). With reduced motion
 * requested nothing animates.
 */

/** Past this many categories value labels crowd each other; the table has them. */
export const LABELLED_ROWS_MAX = 12;

const AXIS_TICK = { fill: "var(--fg-muted)", fontSize: 11 };

const TOOLTIP_STYLE = {
	background: "var(--bg-raised)",
	border: "1px solid var(--border)",
	borderRadius: 6,
	color: "var(--fg)",
	fontSize: 12,
};

interface Frame {
	rtl: boolean;
	animate: boolean;
}

function useFrame(): Frame {
	return { rtl: isRtl(), animate: !useMediaQuery(REDUCED_MOTION_QUERY) };
}

/** The grid, both axes and the tooltip every cartesian chart shares. */
function axes(data: ChartData, frame: Frame, secondary?: { format: (value: number) => string }) {
	const side = cartesianAxes(frame.rtl);
	const format = (value: unknown) => (typeof value === "number" ? data.format(value) : "");
	return [
		<CartesianGrid key="grid" stroke="var(--viz-grid)" vertical={false} />,
		<XAxis
			key="x"
			dataKey={data.x.key}
			reversed={side.reversed}
			stroke="var(--viz-axis)"
			tick={AXIS_TICK}
			tickLine={false}
			{...(data.x.format === undefined
				? {}
				: { tickFormatter: (value: unknown) => data.x.format?.(String(value)) ?? "" })}
		/>,
		<YAxis
			key="y"
			yAxisId="value"
			orientation={side.valueSide}
			stroke="var(--viz-axis)"
			tick={AXIS_TICK}
			tickLine={false}
			tickFormatter={format}
			width={56}
		/>,
		...(secondary === undefined
			? []
			: [
					<YAxis
						key="y2"
						yAxisId="secondary"
						orientation={side.secondarySide}
						stroke="var(--viz-axis)"
						tick={AXIS_TICK}
						tickLine={false}
						tickFormatter={(value: unknown) =>
							typeof value === "number" ? secondary.format(value) : ""
						}
						width={56}
					/>,
				]),
		<Tooltip
			key="tooltip"
			contentStyle={TOOLTIP_STYLE}
			labelStyle={{ color: "var(--fg)" }}
			cursor={{ fill: "var(--viz-grid)" }}
			formatter={(value: unknown) => format(value)}
			{...(data.x.format === undefined
				? {}
				: { labelFormatter: (value: unknown) => data.x.format?.(String(value)) ?? "" })}
		/>,
	];
}

function legend(series: ChartSeries[]) {
	return series.length > 1 ? (
		<Legend wrapperStyle={{ color: "var(--fg-muted)", fontSize: 12 }} iconSize={10} />
	) : null;
}

export interface LineTrendProps extends ChartData {
	/** A band between two row fields, drawn under the lines: p50 to p90. */
	band?: { low: string; high: string; label: string };
	/** A dashed horizontal target. */
	target?: { value: number; label: string };
	height?: number;
}

/** Lines over days, the first solid and the rest dashed, with an optional band and target. */
export function LineTrend({ band, target, height = 240, ...data }: LineTrendProps) {
	const frame = useFrame();
	return (
		<ComposedChart
			data={data.rows}
			responsive
			style={{ width: "100%", height }}
			accessibilityLayer={false}
			margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
		>
			{axes(data, frame)}
			{band !== undefined && (
				<Area
					yAxisId="value"
					dataKey={(row: ChartRow) => [row[band.low], row[band.high]]}
					name={band.label}
					fill="var(--viz-seq-2)"
					stroke="none"
					isAnimationActive={frame.animate}
				/>
			)}
			{data.series.map((series, index) => (
				<Line
					key={series.key}
					yAxisId="value"
					dataKey={series.key}
					name={series.label}
					stroke={seriesColor(series, index)}
					strokeWidth={2}
					strokeDasharray={dashFor(index)}
					dot={false}
					isAnimationActive={frame.animate}
				/>
			))}
			{target !== undefined && (
				<ReferenceLine
					yAxisId="value"
					y={target.value}
					stroke="var(--viz-target)"
					strokeDasharray="4 4"
					label={{
						value: target.label,
						fill: "var(--fg-muted)",
						fontSize: 11,
						position: frame.rtl ? "insideTopLeft" : "insideTopRight",
					}}
				/>
			)}
			{legend(data.series)}
		</ComposedChart>
	);
}

export interface BarSeriesProps extends ChartData {
	/** Side by side (the default) or one on top of the other. */
	mode?: "grouped" | "stacked";
	/** A line over the bars on its own axis: margin over revenue. */
	line?: ChartSeries & { format: (value: number) => string };
	height?: number;
	/** Gap between categories; a histogram's bars touch. */
	categoryGap?: string | number;
	/**
	 * A bar's row when it is clicked: a drill-down. Pointer only, so the
	 * caller also offers the same choice as buttons.
	 */
	onSelect?: (row: ChartRow) => void;
}

/** Grouped or stacked bars, optionally with a line on a second axis. */
export function BarSeries({
	mode = "grouped",
	line,
	height = 240,
	categoryGap = "20%",
	onSelect,
	...data
}: BarSeriesProps) {
	const frame = useFrame();
	const labelled = mode === "grouped" && data.rows.length <= LABELLED_ROWS_MAX;
	return (
		<ComposedChart
			data={data.rows}
			responsive
			style={{ width: "100%", height }}
			accessibilityLayer={false}
			barCategoryGap={categoryGap}
			margin={{ top: 16, right: 8, bottom: 0, left: 8 }}
		>
			{axes(data, frame, line)}
			{data.series.map((series, index) => (
				<Bar
					key={series.key}
					yAxisId="value"
					dataKey={series.key}
					name={series.label}
					fill={seriesColor(series, index)}
					{...(mode === "stacked" ? { stackId: "stack" } : {})}
					{...(onSelect === undefined
						? {}
						: {
								cursor: "pointer",
								onClick: (entry: { payload?: ChartRow }) =>
									entry.payload && onSelect(entry.payload),
							})}
					isAnimationActive={frame.animate}
				>
					{labelled && (
						<LabelList
							dataKey={series.key}
							position="top"
							fill="var(--fg-muted)"
							fontSize={10}
							formatter={(value: unknown) => (typeof value === "number" ? data.format(value) : "")}
						/>
					)}
				</Bar>
			))}
			{line !== undefined && (
				<Line
					yAxisId="secondary"
					dataKey={line.key}
					name={line.label}
					stroke={seriesColor(line, data.series.length)}
					strokeWidth={2}
					strokeDasharray={dashFor(1)}
					dot={false}
					isAnimationActive={frame.animate}
				/>
			)}
			{legend(line === undefined ? data.series : [...data.series, line])}
		</ComposedChart>
	);
}

/** Bucket counts (cycle time, time to deliver), bars touching. */
export function HistogramBars(props: Omit<BarSeriesProps, "mode" | "line" | "categoryGap">) {
	return <BarSeries {...props} categoryGap={1} />;
}
